import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Grid from '../../components/Grid';
import messages from './messages';
import AndOrButtonSet from './andOrButtonSet';
import { getSummaryMessage, isUsableValue } from './utils';
import { classRankOptions, chevronIcon } from './constants';
import './styles.css';
import Dropdown from '../../components/Dropdown';
import Label from '../../components/Label';
import Icon from '../../components/Icon';

let changes;
const blurred = {
  gpaMin: false,
  gpaMax: false,
  classRankMax: false,
  classRankMin: false,
  satMin: false,
  satMax: false,
  actMin: false,
  actMax: false,
};

const localOnChange = (e, name, onChange) => {
  const { value } = e.target;
  changes[name] = value;
  onChange({ target: { value } }, name);
};

const onBlur = (e, name, onChange) => {
  blurred[name] = true;
  onChange({ target: { value: changes[name] } }, name);
  changes[name] = '';
};
/* istanbul ignore next */
const onFocus = (e, name) => {
  changes[name] = e.target.value;
  blurred[name] = false;
};

const getMinError = (errors, scoreType) => (blurred[`${scoreType}Min`] ? errors[`${scoreType}MinError`] : null);
// eslint-disable-next-line max-len
const getMaxError = (errors, scoreType) => ((!getMinError(errors, scoreType) && blurred[`${scoreType}Max`]) ? errors[`${scoreType}MaxError`] : null);
const getRangeError = (errors, scoreType, blurCheck = true) => (
  (
    !getMinError(errors) && !getMaxError(errors) && (!blurCheck || (blurred[`${scoreType}Min`] && blurred[`${scoreType}Max`]))
  ) ? errors[`${scoreType}RangeError`] : null
);

const getErrorToolTip = (errorMsg, errorType, className = '') => (errorMsg ? (
  <Label role="tooltip" basic color="red" pointing data-cy={`merit-scholarships-${errorType}-label`} styleName={classnames('merit-scholarship-requirement-error-tooltip', className)}>
    <Icon name="exclamation circle" styleName="validationError" size="large" />
    {errorMsg}
  </Label>
) : null);

const Requirements = ({
  index,
  gpaMin,
  gpaMax,
  gpaRankMatch,
  classRankMax,
  classRankMin,
  satGpaMatch,
  satMin,
  satMax,
  satActMatch,
  actMin,
  actMax,
  onChange,
  errors,
  forceShowErrors,
}) => {
  changes = { gpaMin, gpaMax, classRankMax, classRankMin, satMin, satMax, actMin, actMax };
  return (
    <React.Fragment>
      <Grid.Row>
        <Grid.Column width={5}>
          <b><FormattedMessage {...messages.meritScholarshipsRequirementsLabel} /></b>
          <span styleName="required">&#x002a;</span>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          <FormattedMessage {...messages.meritScholarshipsRequirementsTextLabel} />
        </Grid.Column>
      </Grid.Row>
      { (forceShowErrors && errors.atLeast1Requirement) && (
        <Grid.Row styleName="merit-scholarships-at-least-one-requirements-error-row">
          <Grid.Column width={16}>
            {getErrorToolTip(errors.atLeast1Requirement, 'atleastone-error', 'atleastone-error')}
          </Grid.Column>
        </Grid.Row>
      )}
      <Grid.Row centered>
        <Grid.Column largeScreen={16} computer={16} tablet={16} mobile={16}>
          <Grid>
            <Grid.Row styleName="merit-scholarships-requirements-row">
              <Grid.Column textAlign="center" largeScreen={6} computer={6} tablet={6} mobile={16}>
                <Grid styleName="merit-scholarship-inner-grid">
                  <Grid.Row centered>
                    <Grid.Column largeScreen={6} computer={6} tablet={4} mobile={8} styleName="merit-scholarships-requirements-label-column">
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsGPALabel} /></div>
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsGPAScaleLabel} /></div>
                    </Grid.Column>
                    <Grid.Column largeScreen={10} computer={10} tablet={8} mobile={8} styleName="merit-scholarships-requirements-column">
                      <Grid styleName="merit-scholarship-inner-grid">
                        <Grid.Row centered>
                          <Grid.Column largeScreen={7} computer={7} tablet={7} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-GPA-min-${index}`}
                              data-cy="merit-scholarships-GPA-min"
                              aria-label="Minimum GPA required"
                              styleName="merit-scholarships-form-input"
                              value={errors.gpaMinError || isUsableValue(gpaMin) ? gpaMin : ''}
                              onChange={(e) => localOnChange(e, 'gpaMin', onChange)}
                              onBlur={(e) => onBlur(e, 'gpaMin', onChange)}
                              onFocus={(e) => onFocus(e, 'gpaMin')}
                            />
                            {getErrorToolTip(getMinError(errors, 'gpa'), 'gpa-error')}
                            {getErrorToolTip(getRangeError(errors, 'gpa'), 'gpa-error')}
                          </Grid.Column>
                          <Grid.Column textAlign="center" largeScreen={2} computer={2} tablet={8} mobile={4} styleName="merit-scholarships-requirements-dash-column">
                            <span> - </span>
                          </Grid.Column>
                          <Grid.Column largeScreen={7} computer={7} tablet={7} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-GPA-max-${index}`}
                              data-cy="merit-scholarships-GPA-max"
                              aria-label="Maximum GPA required"
                              styleName="merit-scholarships-form-input"
                              value={errors.gpaMaxError || (isUsableValue(gpaMin) && isUsableValue(gpaMax)) ? gpaMax : ''}
                              onChange={(e) => localOnChange(e, 'gpaMax', onChange)}
                              onBlur={(e) => onBlur(e, 'gpaMax', onChange)}
                              onFocus={(e) => onFocus(e, 'gpaMax')}
                              disabled={!isUsableValue(gpaMin)}
                            />
                            {getErrorToolTip(getMaxError(errors, 'gpa'), 'gpa-error')}
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              <Grid.Column textAlign="center" largeScreen={2} computer={2} tablet={2} mobile={16}>
                <AndOrButtonSet index={index} matchKeyName="gpa-rank" matchPropertyName="gpaRankMatch" matchProperty={gpaRankMatch} color="green" onChange={onChange} width={2} />
              </Grid.Column>
              <Grid.Column largeScreen={8} computer={8} tablet={8} mobile={16}>
                <Grid styleName="merit-scholarship-inner-grid">
                  <Grid.Row>
                    <Grid.Column largeScreen={4} computer={5} tablet={4} mobile={16} styleName="merit-scholarships-requirements-label-column">
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsClassRankLabel} /></div>
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsClassRankScaleLabel} /></div>
                    </Grid.Column>
                    <Grid.Column largeScreen={12} computer={11} tablet={12} mobile={16} styleName="merit-scholarships-requirements-column">
                      <Grid styleName="merit-scholarship-inner-grid">
                        <Grid.Row>
                          <Grid.Column largeScreen={8} computer={8} tablet={10} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input-dropdown">
                            <Dropdown
                              key={`scholarships-max-class-rank-dropdown-${index}`}
                              data-cy="merit-scholarships-max-class-rank-dropdown"
                              styleName="merit-scholarships-class-rank-dropdown"
                              aria-label="Choose Class Rank"
                              value={classRankMax || 0}
                              options={classRankOptions.map((o) => ({ ...o, 'aria-label': o.value }))}
                              onChange={(e, { value }) => localOnChange({ target: { value } }, 'classRankMax', onChange)}
                              onBlur={(e) => onBlur(e, 'classRankMax', onChange)}
                              onFocus={(e) => onFocus(e, 'classRankMax')}
                              icon={chevronIcon}
                              selection
                            />
                            {getErrorToolTip(getRangeError(errors, 'classRank', false), 'class-rank-error', 'class-rank-range-error')}
                          </Grid.Column>
                          <Grid.Column largeScreen={1} computer={1} tablet={2} mobile={4} styleName="merit-scholarships-requirements-dash-column-CR">
                            <span> - </span>
                          </Grid.Column>
                          <Grid.Column largeScreen={7} computer={7} tablet={6} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input-dropdown">
                            <Dropdown
                              key={`scholarships-min-class-rank-dropdown-${index}`}
                              data-cy="merit-scholarships-min-class-rank-dropdown"
                              styleName="merit-scholarships-class-rank-dropdown"
                              aria-label="Choose Class Rank"
                              value={classRankMin || 0}
                              // eslint-disable-next-line quote-props
                              options={classRankOptions.map((o) => ({ ...o, 'aria-label': o.value, disabled: (o.value >= classRankMax) }))}
                              onChange={(e, { value }) => localOnChange({ target: { value } }, 'classRankMin', onChange)}
                              onBlur={(e) => onBlur(e, 'classRankMin', onChange)}
                              onFocus={(e) => onFocus(e, 'classRankMin')}
                              icon={chevronIcon}
                              disabled={!isUsableValue(classRankMax)}
                              selection
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row centered>
        <AndOrButtonSet index={index} matchKeyName="sat-gpa" matchPropertyName="satGpaMatch" matchProperty={satGpaMatch} color="red" onChange={onChange} width={11} />
      </Grid.Row>
      <Grid.Row centered>
        <Grid.Column largeScreen={16} computer={16} tablet={16} mobile={16}>
          <Grid>
            <Grid.Row styleName="merit-scholarships-requirements-row">
              <Grid.Column textAlign="center" largeScreen={6} computer={6} tablet={6} mobile={16}>
                <Grid styleName="merit-scholarship-inner-grid">
                  <Grid.Row centered>
                    <Grid.Column largeScreen={6} computer={6} tablet={4} mobile={8} styleName="merit-scholarships-requirements-label-column">
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsSATLabel} /></div>
                      <div styleName="merit-scholarships-requirement-label"><FormattedMessage {...messages.meritScholarshipsSATScaleLabel} /></div>
                    </Grid.Column>
                    <Grid.Column largeScreen={10} computer={10} tablet={8} mobile={8} styleName="merit-scholarships-requirements-column">
                      <Grid stackable styleName="merit-scholarship-inner-grid">
                        <Grid.Row centered>
                          <Grid.Column largeScreen={7} computer={7} tablet={7} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-SAT-min-${index}`}
                              data-cy="merit-scholarships-SAT-min"
                              aria-label="Minimum SAT score required"
                              styleName="merit-scholarships-form-input"
                              value={isUsableValue(satMin) ? satMin : ''}
                              onChange={(e) => localOnChange(e, 'satMin', onChange)}
                              onBlur={(e) => onBlur(e, 'satMin', onChange)}
                              onFocus={(e) => onFocus(e, 'satMin')}
                            />
                            {getErrorToolTip(getMinError(errors, 'sat'), 'sat-error')}
                            {getErrorToolTip(getRangeError(errors, 'sat'), 'sat-error')}
                          </Grid.Column>
                          <Grid.Column textAlign="center" largeScreen={2} computer={2} tablet={8} mobile={4} styleName="merit-scholarships-requirements-dash-column">
                            <span> - </span>
                          </Grid.Column>
                          <Grid.Column largeScreen={7} computer={7} tablet={7} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-SAT-max-${index}`}
                              data-cy="merit-scholarships-SAT-max"
                              aria-label="Maximum SAT score required"
                              styleName="merit-scholarships-form-input"
                              value={isUsableValue(satMin) && isUsableValue(satMax) ? satMax : ''}
                              onChange={(e) => localOnChange(e, 'satMax', onChange)}
                              onBlur={(e) => onBlur(e, 'satMax', onChange)}
                              onFocus={(e) => onFocus(e, 'satMax')}
                              disabled={!isUsableValue(satMin)}
                            />
                            {getErrorToolTip(getMaxError(errors, 'sat'), 'sat-error')}
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              <Grid.Column textAlign="center" largeScreen={2} computer={2} tablet={2} mobile={16}>
                <AndOrButtonSet index={index} matchKeyName="sat-act" matchPropertyName="satActMatch" matchProperty={satActMatch} color="green" onChange={onChange} width={2} />
              </Grid.Column>
              <Grid.Column largeScreen={8} computer={8} tablet={8} mobile={16}>
                <Grid styleName="merit-scholarship-inner-grid">
                  <Grid.Row>
                    <Grid.Column largeScreen={5} computer={5} tablet={4} mobile={16} styleName="merit-scholarships-requirements-label-column">
                      <div styleName="merit-scholarships-requirement-label act-label"><FormattedMessage {...messages.meritScholarshipsACTLabel} /></div>
                    </Grid.Column>
                    <Grid.Column largeScreen={11} computer={8} tablet={12} mobile={16} styleName="merit-scholarships-requirements-column">
                      <Grid styleName="merit-scholarship-inner-grid">
                        <Grid.Row>
                          <Grid.Column largeScreen={7} computer={7} tablet={10} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-ACT-min-${index}`}
                              data-cy="merit-scholarships-ACT-min"
                              aria-label="Minimum ACT score required"
                              styleName="merit-scholarships-form-input"
                              value={isUsableValue(actMin) ? actMin : ''}
                              onChange={(e) => localOnChange(e, 'actMin', onChange)}
                              onBlur={(e) => onBlur(e, 'actMin', onChange)}
                              onFocus={(e) => onFocus(e, 'actMin')}
                            />
                            {getErrorToolTip(getMinError(errors, 'act'), 'act-error')}
                            {getErrorToolTip(getRangeError(errors, 'act'), 'act-error')}
                          </Grid.Column>
                          <Grid.Column largeScreen={2} computer={2} tablet={4} mobile={4} styleName="merit-scholarships-requirements-dash-column">
                            <span> - </span>
                          </Grid.Column>
                          <Grid.Column largeScreen={7} computer={7} tablet={6} mobile={12} styleName="merit-scholarships-requirements-column max-width-setting-input">
                            <input
                              key={`scholarship-input-ACT-max-${index}`}
                              data-cy="merit-scholarships-ACT-max"
                              aria-label="Maximum ACT score required"
                              styleName="merit-scholarships-form-input"
                              value={isUsableValue(actMin) && isUsableValue(actMax) ? actMax : ''}
                              onChange={(e) => localOnChange(e, 'actMax', onChange)}
                              onBlur={(e) => onBlur(e, 'actMax', onChange)}
                              onFocus={(e) => onFocus(e, 'actMax')}
                              disabled={!isUsableValue(actMin)}
                            />
                            {getErrorToolTip(getMaxError(errors, 'act'), 'act-error')}
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          {getSummaryMessage(
            gpaMin,
            gpaMax,
            classRankMin,
            classRankMax,
            satMin,
            satMax,
            actMin,
            actMax,
            gpaRankMatch,
            satGpaMatch,
            satActMatch,
          )}
        </Grid.Column>
      </Grid.Row>
    </React.Fragment>
  );
};

Requirements.propTypes = {
  index: PropTypes.string,
  gpaMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gpaMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gpaRankMatch: PropTypes.string,
  classRankMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classRankMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  satGpaMatch: PropTypes.string,
  satMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  satMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  satActMatch: PropTypes.string,
  actMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  actMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  errors: PropTypes.shape({
    gpaMinError: PropTypes.string,
    gpaMaxError: PropTypes.string,
    gpaRangeError: PropTypes.string,
    classRankRangeError: PropTypes.string,
    satMinError: PropTypes.string,
    satMaxError: PropTypes.string,
    satRangeError: PropTypes.string,
    actMinError: PropTypes.string,
    actMaxError: PropTypes.string,
    actRangeError: PropTypes.string,
    atLeast1Requirement: PropTypes.string,
  }),
  forceShowErrors: PropTypes.bool,
};

export default Requirements;
